import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome to Florvanta, ${user.name}!`);
      nav(form.role === "vendor" ? "/sell" : "/");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="rounded-2xl border border-[#E2E8E5] bg-white p-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Join Florvanta</div>
        <h1 className="font-display text-3xl text-[#0F3D2E] mt-1">Create your account</h1>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          <div>
            <label className="text-xs text-[#3B5249]">Full name</label>
            <Input required value={form.name} onChange={upd("name")} className="mt-1" data-testid="reg-name" />
          </div>
          <div>
            <label className="text-xs text-[#3B5249]">Email</label>
            <Input type="email" required value={form.email} onChange={upd("email")} className="mt-1" data-testid="reg-email" />
          </div>
          <div>
            <label className="text-xs text-[#3B5249]">Password (min 6 chars)</label>
            <Input type="password" required minLength={6} value={form.password} onChange={upd("password")} className="mt-1" data-testid="reg-password" />
          </div>
          <div>
            <label className="text-xs text-[#3B5249]">I want to</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {[["buyer","Shop"],["vendor","Sell"]].map(([v,l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm({...form, role: v})}
                  data-testid={`role-${v}`}
                  className={`h-10 rounded-full border text-sm transition-colors ${form.role===v ? "bg-[#0F3D2E] text-white border-[#0F3D2E]" : "bg-white text-[#0F3D2E] border-[#E2E8E5] hover:border-[#14A085]"}`}
                >
                  {l} on Florvanta
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#0F3D2E] hover:bg-[#14A085] text-white rounded-full" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-[#3B5249]">
          Already have an account?{" "}
          <Link to="/login" className="text-[#14A085] hover:underline" data-testid="link-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
